import { useEffect, useRef, useState } from 'react';

import InfiniteScroll from '../../../../../../components/InfiniteScroll/InfiniteScroll';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { Button } from '../../../../../../components/Clickables/Buttons/Button';
import { SaveButton } from '../../../../../../components/Clickables/Buttons/SaveButton';
import { DownloadAppMapDrawerCTA } from '../../../../../../components/CTAs/DownloadAppMapDrawerCTA/DownloadAppMapDrawerCTA';
import { DotIcon } from '../../../../../../components/Icons/DotIcon';
import { FilterIcon } from '../../../../../../components/Icons/FilterIcon';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { PostsViewer } from '../../../../../../components/PostsViewer/PostsViewer';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { reportMykissClientError } from '@lib/errors/client';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { getBoundingBoxFromLocation, useMapLocation } from '@domain/map/useMapLocation';
import { usePostViewer } from '@domain/posts/usePostViewer';
import { useUserInfo } from '@domain/user/useUserInfo';
import { getCatchesInMapBoundingBox } from '@graphql/queries/rutilus/Catches';
import {
  CATCHES_PER_REQUEST,
  CATCHES_PER_REQUEST_FREE,
  ZOOM_IN_LIMIT,
  ZOOM_OUT_LIMIT,
} from '../../../../constants/constants';
import { useExplore } from '../../../../context/ExploreContext';
import { getMonthArrayFromUrl, getSpeciesIdArrayFromUrl } from '../../../../context/helpers';
import { getFilterCount } from '../../helpers';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';
import { MapDrawerSignup } from '../../MapDrawerSignup/MapDrawerSignup';

import { CatchesViewList } from './CatchesViewList';
import { CatchesViewHeader } from './components/CatchesViewHeader';

export const CatchesView = () => {
  const location = useMapLocation();
  const { isPremium, isLoggedIn } = useUserInfo();
  const { currentPage } = useAnalytics();
  const { exploreQueryParams, saveParamsInUrl, setFilterViewBackButtonPath } = useExplore();
  const { catchFiltersMonths, catchFiltersSpecies } = exploreQueryParams;
  const catchesFilterMonthArray = getMonthArrayFromUrl(catchFiltersMonths);
  const catchesFilterSpeciesIdArray = getSpeciesIdArrayFromUrl(catchFiltersSpecies);
  const filterCount = getFilterCount({
    speciesIds: catchesFilterSpeciesIdArray,
    months: catchesFilterMonthArray,
  });
  const postsReference = useRef<HTMLDivElement>(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const {
    currentPostViewerId,
    isPostViewerOpen,
    setCurrentPostViewerId,
    openPostViewer,
    closePostViewer,
  } = usePostViewer();
  const boundingBox = getBoundingBoxFromLocation(location);

  const isTooZoomedIn = !!location && location.zoom > ZOOM_IN_LIMIT;
  const isTooZoomedOut = !!location && location.zoom < ZOOM_OUT_LIMIT;
  const isTooZoomedInAndNotPro = isTooZoomedIn && !isPremium;

  // We block users from zooming right in if not pro to prevent a pro leak
  // If we don't block them they can zoom all the way in to pinpoint the catch
  const skipRutilusQuery =
    !location || boundingBox === null || isTooZoomedOut || isTooZoomedInAndNotPro;

  const { loading, data, fetchMore, refetch } = useRutilusQuery(getCatchesInMapBoundingBox, {
    skip: skipRutilusQuery,
    variables: {
      boundingBox,
      first: isLoggedIn ? CATCHES_PER_REQUEST : CATCHES_PER_REQUEST_FREE,
      caughtInMonths: catchesFilterMonthArray.length >= 1 ? catchesFilterMonthArray : undefined,
      speciesIds: catchesFilterSpeciesIdArray.length >= 1 ? catchesFilterSpeciesIdArray : undefined,
    },
    notifyOnNetworkStatusChange: true,
  });

  // // TODO: make useRutilusQuery reactive and remove this useEffect
  // // refresh catches data after filter changes
  useEffect(() => {
    if (boundingBox?.northEast.latitude && !skipRutilusQuery) {
      refetch({ boundingBox }).catch(reportMykissClientError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchFiltersMonths, catchFiltersSpecies, refetch, boundingBox?.northEast.latitude]);

  if ((!data && loading) || !location || !boundingBox) {
    return (
      <MapDrawerLayout headerContent={<CatchesViewHeader />}>
        <LoadingSpinner loadingText="Loading catches in the area..." />
      </MapDrawerLayout>
    );
  }

  if (isTooZoomedOut) {
    return (
      <MapDrawerLayout headerContent={<CatchesViewHeader />}>
        <MapDrawerNotification
          title="Zoom in to show catches"
          description="Try zooming in to get more results"
          withIcon
        />
      </MapDrawerLayout>
    );
  }

  if (isTooZoomedInAndNotPro) {
    return (
      <MapDrawerLayout
        headerContent={<CatchesViewHeader isTooZoomedInAndNotPro={isTooZoomedInAndNotPro} />}
      >
        <DownloadAppMapDrawerCTA />
      </MapDrawerLayout>
    );
  }

  if (!data) {
    return (
      <MapDrawerLayout headerContent={<CatchesViewHeader />}>
        <MapDrawerNotification
          title="No catches in this area"
          description="Try another area or zooming out to get more results"
          withIcon
        />
      </MapDrawerLayout>
    );
  }

  const hasNextPage = data.mapArea.catches.pageInfo.hasNextPage;
  const endCursor = data.mapArea.catches.pageInfo.endCursor;
  const canLoadMore = isLoggedIn && !loading && hasNextPage && !isFetchingMore;
  const totalCatchesCount = data.mapArea.catches.totalCount;

  const fetchNextPage = async (): Promise<unknown> => {
    setIsFetchingMore(true);
    trackEvent({
      name: 'mykiss_load_more',
      properties: {
        page: currentPage,
        manual: true,
        source: 'explore_catches',
      },
    });

    return fetchMore({
      variables: {
        after: endCursor,
        first: CATCHES_PER_REQUEST,
        caughtInMonths: catchesFilterMonthArray.length >= 1 ? catchesFilterMonthArray : undefined,
        speciesIds:
          catchesFilterSpeciesIdArray.length >= 1 ? catchesFilterSpeciesIdArray : undefined,
      },
    })
      .then(() => {
        setIsFetchingMore(false);
      })
      .catch(reportMykissClientError);
  };

  const catchPostList = getNodesFromConnection(data.mapArea.catches).map(_catch => _catch.post);

  const fullWidthHeaderContent = (
    <div className="mb-2 h-18 w-full">
      <Button
        variant="buttonSecondaryVariant"
        className="w-full"
        onClick={() => {
          setFilterViewBackButtonPath('catches');
          saveParamsInUrl({
            drawerView: 'filter-catches',
            drawerViewId: undefined,
          });
        }}
        size="sm"
      >
        Filter {filterCount > 0 ? `(${filterCount})` : ''}
        {filterCount ? (
          <div className="grid">
            <DotIcon className="z-high text-pinktail col-start-1 row-start-1 ml-5" />
            <span className="col-start-1 row-start-1 ml-[6px]">
              <FilterIcon />
            </span>
          </div>
        ) : (
          <span className="ml-[6px]">
            <FilterIcon />
          </span>
        )}
      </Button>
    </div>
  );

  return (
    <MapDrawerLayout
      drawerRef={postsReference}
      headerContent={<CatchesViewHeader totalCount={data.mapArea.catches.totalCount} />}
      fullWidthHeaderContent={fullWidthHeaderContent}
      fullHeight={false}
      contentId="catches-view"
    >
      {!isLoggedIn && (
        <MapDrawerSignup
          headingText="See all catches in the Fishbrain app"
          bodyText="In the app you can see all catches in this area"
          downloadButtonText="Download the app"
        />
      )}
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={catchPostList.length}
        hasMore={hasNextPage}
        scrollableTarget="catches-view"
        loader={<></>}
      >
        <div className="flex h-full flex-col">
          {totalCatchesCount > 0 ? (
            <div className="h-full">
              <CatchesViewList catchPosts={catchPostList} openPostViewer={openPostViewer} />

              {totalCatchesCount < CATCHES_PER_REQUEST && filterCount === 0 && (
                <MapDrawerNotification
                  title="Looking for more?"
                  description="Try another area or zooming out to get more results"
                />
              )}

              {totalCatchesCount < CATCHES_PER_REQUEST && filterCount > 0 && (
                <MapDrawerNotification
                  title="Find more catches in your area"
                  description="To get more results, try adjusting one of your active search filters"
                  buttonText={`Remove all filters (${filterCount} active)`}
                  onClick={() => {
                    saveParamsInUrl({
                      catchFiltersSpecies: undefined,
                      catchFiltersMonths: undefined,
                    });
                  }}
                />
              )}

              {hasNextPage && isLoggedIn && (
                <div className="my-8 flex w-full justify-center">
                  <SaveButton
                    onClick={() => {
                      void fetchNextPage();
                    }}
                    variant="buttonPrimary"
                    savingLabel="Loading..."
                    isSaving={loading}
                  >
                    Load more
                  </SaveButton>
                </div>
              )}
            </div>
          ) : (
            <div>
              {filterCount === 0 && (
                <MapDrawerNotification
                  title="No catches in this area"
                  description="Try another area or zooming out to get more results"
                  withIcon
                />
              )}

              {filterCount > 0 && (
                <MapDrawerNotification
                  title="No results"
                  description="To get more results, try adjusting your filters or zooming out on the map"
                  buttonText={`Remove all filters (${filterCount} active)`}
                  onClick={() => {
                    saveParamsInUrl({
                      catchFiltersSpecies: undefined,
                      catchFiltersMonths: undefined,
                    });
                  }}
                />
              )}
            </div>
          )}
        </div>
      </InfiniteScroll>
      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={catchPostList}
          currentPostViewerId={currentPostViewerId}
          hasMore={canLoadMore}
          isPostViewerOpen
          loadMore={() => {
            void fetchNextPage();
          }}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
        />
      )}
    </MapDrawerLayout>
  );
};
